<template>
    <div class="indexPage" @click.capture="onPageFirstGesture" @pointerdown.capture="onPageFirstGesture" @touchend.capture="onPageFirstGesture">
        <div class="page-shell">
            <div class="page-background">
                <img v-for="(item, index) in backgroundStack" :key="item.src" class="page-background__image" :src="item.src" alt="" :loading="index === 0 ? 'eager' : 'lazy'" decoding="async" :fetchpriority="index === 0 ? 'high' : 'auto'" />
            </div>

            <div class="page-foreground">
                <section class="hero-section content-section" :class="{ 'section-visible': sectionVisible.P1 }" ref="section-P1">
                    <img class="hero-section__nav fade-item" :style="heroFadeStyle(0)" :src="hero.nav" alt="" loading="eager" decoding="sync" fetchpriority="high" />
                    <div class="hero-section__title-wrap fade-item" ref="trigger-P1" :style="heroFadeStyle(1)">
                        <img class="hero-section__title" :src="hero.title" alt="" loading="eager" decoding="sync" fetchpriority="high" />
                    </div>

                    <div class="hero-video-group fade-item" :style="heroFadeStyle(2)">
                        <!-- 首屏内嵌视频：对齐 2026XC70Curling（内嵌自动播放 + 自定义控件 + 封面遮罩） -->
                        <div class="hero-inline-video">
                            <video
                                ref="heroVideo"
                                class="hero-video"
                                :controls="heroVideoUseNativeControls"
                                autoplay
                                preload="auto"
                                :src="heroVideoUrl"
                                :poster="hero.videoCover"
                                :muted="heroVideoMuted"
                                webkit-playsinline
                                playsinline
                                x5-playsinline
                                x5-video-player-type="h5"
                                x5-video-player-fullscreen="true"
                                x5-video-orientation="portrait"
                                x-webkit-airplay="allow"
                                @loadedmetadata="onHeroVideoLoadedMeta"
                                @loadeddata="onHeroVideoLoadedData"
                                @canplay="onHeroVideoCanPlay"
                                @timeupdate="onHeroVideoTimeUpdate"
                                @play="onHeroVideoPlay"
                                @pause="onHeroVideoPause"
                                @ended="onHeroVideoEnded"
                            ></video>

                            <div v-show="!heroVideoUseNativeControls" class="hero-controls-container">
                                <div class="progress-container" @click="onHeroVideoProgressClick">
                                    <div class="progress-bar sm-bar" :style="{ width: heroVideoProgressPercent + '%' }"></div>
                                </div>
                                <div class="controls-row">
                                    <div class="left-controls">
                                        <button
                                            class="control-btn play-pause play-pause0"
                                            :class="heroVideoPaused ? 'paused' : 'playing'"
                                            @click.stop="toggleHeroVideoPlayPause"
                                        ></button>
                                        <span class="time-display current-time sm-time">{{ heroVideoCurrentTimeText }}</span>
                                        <span class="time-display">/</span>
                                        <span class="time-display duration">{{ heroVideoDurationText }}</span>
                                    </div>
                                    <div class="right-controls">
                                        <div class="volume-container">
                                            <button
                                                class="control-btn volume-btn"
                                                :class="heroVideoMuted ? 'mutedTrue' : 'mutedFalse'"
                                                @click.stop="toggleHeroVideoMute"
                                            ></button>
                                        </div>
                                        <button class="control-btn fullscreen-btn" :class="{ full: heroVideoIsFullscreen }" @click.stop="toggleHeroVideoFullscreen"></button>
                                    </div>
                                </div>
                            </div>

                            <!-- 封面遮罩：点击触发播放/解静音（Curling 的 page1VideoBtn 思路） -->
                            <div
                                v-show="heroVideoShowOverlay"
                                class="hero-video-overlay"
                                :style="{ backgroundImage: `url(${hero.videoCover})` }"
                                @click.stop="onHeroVideoOverlayClick"
                                @touchend.stop.prevent="onHeroVideoOverlayClick"
                            ></div>

                            <!-- 画框与播放图标（保持原视觉） -->
                            <!-- <img class="hero-video-frame" :src="hero.videoFrame" alt="" loading="eager" decoding="sync" fetchpriority="high" /> -->
                            <img v-show="heroVideoShowOverlay" class="hero-video-play" :src="hero.playIcon" alt="" loading="eager" decoding="sync" fetchpriority="high" />
                        </div>
                    </div>

                    <img class="hero-section__disclaimer fade-item" :style="heroFadeStyle(3)" :src="hero.disclaimer" alt="" />
                </section>

                <section class="topic-section topic-section--1 content-section" :class="{ 'section-visible': sectionVisible.P2 }" ref="section-P2">
                    <img class="topic-section__title fade-item" ref="trigger-P2" :src="topicSections[0].title" alt="" />
                    <div class="topic-group topic-group--243 fade-item" :style="fadeStyle(1)">
                        <img class="topic-card-group__fallback" :src="topicSections[0].cards[0].image" alt="" />
                        <video :class="['topic-card-group__image', { 'topic-card-group__image--ready': topicCardVideoReady['P2-0'] }]" :poster="topicSections[0].cards[0].image" :src="topicSections[0].cards[0].video" muted loop playsinline webkit-playsinline x5-playsinline preload="auto" @canplay="markTopicCardVideoReady('P2-0')"></video>
                        <img class="topic-card-group__floating-word" :src="topicSections[0].cards[0].caption" alt="" />
                    </div>
                    <!-- P2 第二张卡暂不渲染 -->
                </section>

                <section class="topic-section topic-section--2 content-section" :class="{ 'section-visible': sectionVisible.P3 }" ref="section-P3">
                    <img class="topic-section__title fade-item" ref="trigger-P3" :src="topicSections[1].title" alt="" />
                    <div class="topic-group topic-group--245 fade-item" :style="fadeStyle(1)">
                        <img class="topic-card-group__fallback" :src="topicSections[1].cards[0].image" alt="" />
                        <video :class="['topic-card-group__image', { 'topic-card-group__image--ready': topicCardVideoReady['P3-0'] }]" :poster="topicSections[1].cards[0].image" :src="topicSections[1].cards[0].video" muted loop playsinline webkit-playsinline x5-playsinline preload="auto" @canplay="markTopicCardVideoReady('P3-0')"></video>
                        <img class="topic-card-group__floating-word" :src="topicSections[1].cards[0].caption" alt="" />
                    </div>
                    <!-- P3 第二张卡暂不渲染 -->
                </section>

                <section class="topic-section topic-section--3 content-section" :class="{ 'section-visible': sectionVisible.P4 }" ref="section-P4">
                    <img class="topic-section__title fade-item" ref="trigger-P4" :src="topicSections[2].title" alt="" />
                    <div class="topic-group topic-group--247 fade-item" :style="fadeStyle(1)">
                        <img class="topic-card-group__fallback" :src="topicSections[2].cards[0].image" alt="" />
                        <video :class="['topic-card-group__image', { 'topic-card-group__image--ready': topicCardVideoReady['P4-0'] }]" :poster="topicSections[2].cards[0].image" :src="topicSections[2].cards[0].video" muted loop playsinline webkit-playsinline x5-playsinline preload="auto" @canplay="markTopicCardVideoReady('P4-0')"></video>
                        <img class="topic-card-group__floating-word" :src="topicSections[2].cards[0].caption" alt="" />
                    </div>
                    <!-- P4 第二张卡暂不渲染 -->
                </section>

                <section class="topic-section topic-section--4 content-section" :class="{ 'section-visible': sectionVisible.P5 }" ref="section-P5">
                    <img class="topic-section__title fade-item" ref="trigger-P5" :style="fadeStyle(0)" :src="topicSections[3].title" alt="" />
                    <div class="topic-group topic-group--249 fade-item" :style="fadeStyle(1)">
                        <img class="topic-card-group__fallback" :src="topicSections[3].cards[0].image" alt="" />
                        <video :class="['topic-card-group__image', { 'topic-card-group__image--ready': topicCardVideoReady['P5-0'] }]" :poster="topicSections[3].cards[0].image" :src="topicSections[3].cards[0].video" muted loop playsinline webkit-playsinline x5-playsinline preload="auto" @canplay="markTopicCardVideoReady('P5-0')"></video>
                        <img class="topic-card-group__floating-word" :src="topicSections[3].cards[0].caption" alt="" />
                    </div>
                </section>

                <section class="topic4-section content-section" :class="{ 'section-visible': sectionVisible.P6 }" ref="section-P6">
                    <img class="topic4-section__brand fade-item" ref="trigger-P6" :style="fadeStyle(0)" :src="offer.brandBar" alt="" />

                    <div class="topic4-group topic4-group--242 fade-item" :style="fadeStyle(1)">
                        <img class="topic4-section__title" :src="offer.titleFallback" alt="" />
                    </div>

                    <div class="topic4-group topic4-group--action fade-item" :style="fadeStyle(2)">
                        <button class="topic4-section__cta" @click="goUrl">
                            <img :src="offer.ctaImage" alt="" />
                        </button>
                        <div class="topic4-section__copy-wrap">
                            <img class="topic4-section__copy" :src="offer.copyFallback" alt="" />
                        </div>
                    </div>

                    <div class="topic4-group topic4-group--benefits fade-item" :style="fadeStyle(3)">
                        <img class="topic4-section__policy" :src="offer.policyFallback" alt="" />
                        <div class="topic4-group topic4-group--qr">
                            <img class="topic4-section__tips" :src="offer.tipsFallback" alt="" />
                            <div class="topic4-section__qr-box">
                                <img class="topic4-section__qr" :src="offer.qrFallback" alt="" />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>

        <div class="fixedBox">
            <div :class="['testDriveForm', { hidden: testDriveFormHidden }]">
                <div v-show="showTestTips" class="testTips"></div>
                <div :class="form.guo == 0 ? 'guo' : 'guo active'" @click="toggleGuo"></div>
                <div class="xieyi" @click="toXieyi"></div>
                <button class="drive-btn" @click="submit"></button>
                <div class="form-phone">
                    <input v-model.trim="form.mobile" type="tel" inputmode="numeric" maxlength="11" placeholder="" @focus="handlePhoneFocus" @blur="handlePhoneBlur" />
                </div>
            </div>
        </div>

        <div v-if="showSuccess" class="toast-success" @click="showSuccess = false">
            <div class="toast-success-con">
                <img src="https://campaigns-static.volvocars.com.cn/image/images/cnylx_success_h5.png" alt="" />
            </div>
        </div>
    </div>
</template>

<script>
import { isPhoneNumber, getUrlParam } from '../utils/index'
import { get, post } from '../utils/request'

const TRACK_EVENT = '2026XC70Balloon'

const backgrounds = {
    hero: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon_bg_hero-260404_5.png',
    topic1: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon_bg_topic1-260404_5.png',
    topic2: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon_bg_topic2-260404_5.png',
    topic3: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon_bg_topic3-260404_5.png',
    topic4: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon_bg_topic4-260404_5.png',
    topic5: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon_bg_topic5-260404_5.png',
    bottom: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon_bg_topic6-260404_5.png'
}

const backgroundStack = [{ src: backgrounds.hero }, { src: backgrounds.topic1 }, { src: backgrounds.topic2 }, { src: backgrounds.topic3 }, { src: backgrounds.topic4 }, { src: backgrounds.topic5 }, { src: backgrounds.bottom }]

const hero = {
    nav: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-hero_nav-260404_1.png',
    title: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-hero_title-260404_7.png',
    videoCover: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-hero_videoCover-260404_2.jpg',
    videoFrame: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-hero_videoFrame-260404_1.png',
    playIcon: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-hero_playIcon-260404_1.png',
    disclaimer: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-hero_disclaimer_260404_5.png'
}

const topicSections = [
    {
        name: 'P2',
        title: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p2_title-260404_5.png',
        cards: [
            { image: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p2_card_0_image-260404_5.png', caption: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p2_card_0_caption-260404_5.png', video: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-topic_video_1-260405_6.mp4' },
            // { image: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p2_card_1_image-260404_1.png', caption: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p2_card_1_caption-260404_1.png', video: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-topic_video_2-260405_4.mp4' }
        ]
    },
    {
        name: 'P3',
        title: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p3_title-260404_5.png',
        cards: [
            { image: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p3_card_0_image-260404_5.png', caption: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p3_card_0_caption-260404_5.png', video: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-topic_video_3-260405_6.mp4' },
            // { image: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p3_card_1_image-260404_1.png', caption: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p3_card_1_caption-260404_1.png', video: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-topic_video_4-260405_5.mp4' }
        ]
    },
    {
        name: 'P4',
        title: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p4_title-260404_5.png',
        cards: [
            { image: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p4_card_0_image-260404_5.png', caption: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p4_card_0_caption-260404_5.png', video: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-topic_video_5-260405_6.mp4' },
            // { image: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p4_card_1_image-260404_1.png', caption: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p4_card_1_caption-260404_1.png', video: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-topic_video_6-260405_4.mp4' }
        ]
    },
    {
        name: 'P5',
        title: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p5_title-260404_5.png',
        cards: [
            { image: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p5_card_0_image-260404_5.png', caption: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-topic_p5_card_0_caption-260404_5.png', video: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-topic_video_7-260405_6.mp4' },
        ]
    }
]

const offer = {
    brandBar: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-offer_brandBar-260404_1.png',
    titleFallback: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-offer_title-260404_6.png',
    ctaImage: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-offer_btn-260404_2.png',
    copyFallback: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-offer_ctaImage-260404_6.png',
    policyFallback: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-offer_policy-260404_6.png',
    tipsFallback: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-offer_tips-260404_6.png',
    qrFallback: 'https://campaigns-static.volvocars.com.cn/image/images/2026XC70Balloon-offer_qr-260404_1.png'
}

const sectionConfigs = [{ name: 'P1' }, { name: 'P2' }, { name: 'P3' }, { name: 'P4' }, { name: 'P5' }, { name: 'P6' }]

const initialSectionVisible = sectionConfigs.reduce((acc, section) => {
    acc[section.name] = false
    return acc
}, {})

export default {
    data() {
        return {
            backgrounds,
            backgroundStack,
            hero,
            topicSections,
            offer,
            form: {
                mobile: '',
                guo: 0
            },
            // (legacy) 之前用于弹层视频；现已改为首屏内嵌视频，保留字段避免历史逻辑误引用
            showVideoDialog: false,
            showSuccess: false,
            showFloatingForm: true,
            testDriveFormHidden: false,
            phoneFocused: false,
            scrollTimeout: null,
            sectionVisible: Object.assign({}, initialSectionVisible),
            sectionTriggerVisibleState: Object.assign({}, initialSectionVisible),
            currentSectionName: sectionConfigs[0].name,
            lastTrackedSectionName: '',
            trackedSectionHitTimesMap: {},
            trackedSectionTotalCountMap: {},
            hasEnteredNonP1Section: false,
            topicCardVideoReady: {},
            heroAssetLoaded: 0,
            heroAssetTotal: 0,
            heroLoadTimer: null,
            observer: null,
            topicCardObserver: null,
            heroVideoUrl: 'https://campaigns-static.volvocars.com.cn/file/files/2026XC70Balloon-hero_video-260405_3.mp4',
            heroVideoUseNativeControls: false,
            heroVideoPaused: true,
            heroVideoMuted: true,
            heroVideoIsFullscreen: false,
            heroVideoCurrentTime: 0,
            heroVideoDuration: 0,
            heroVideoFirstInteraction: true,
            heroVideoAutoPlayedOnEnter: false,
            heroVideoShouldAutoplay: false,
            heroVideoShowOverlay: true,
            heroVideoGestureFallbackBound: false,
            heroVideoGestureFallbackDone: false,
            topicCardVideoGestureFallbackBound: false,
            topicCardVideoGestureFallbackDone: false,
            wechatJumpUrl: 'https://wxaurl.cn/prT7BFdnZAp',
            txt11Bg: '',
            txt12Bg: '',
            quanyiBg: '',
            tipsBg: '',
            codeBg: ''
        }
    },
    created() {
        this.getCampaignConfig()
    },
    mounted() {
        this.setupTracking()
        this.setupTopicCardVideoPlayback()
        window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
        window.addEventListener('resize', this.handleWindowResize, { passive: true })
        document.addEventListener('visibilitychange', this.handleVisibilityChange)
        document.addEventListener('fullscreenchange', this.onHeroVideoFullscreenChange)
        document.addEventListener('webkitfullscreenchange', this.onHeroVideoFullscreenChange)
        document.addEventListener('mozfullscreenchange', this.onHeroVideoFullscreenChange)
        document.addEventListener('MSFullscreenChange', this.onHeroVideoFullscreenChange)
        window.addEventListener('beforeunload', this.onBeforeUnloadTrackVideoTime)
        this.bindTopicCardVideoGestureFallback()
        const heroVideo = this.$refs.heroVideo
        if (heroVideo) {
            heroVideo.addEventListener('webkitbeginfullscreen', this.onHeroVideoNativeEnterFullscreen)
            heroVideo.addEventListener('webkitendfullscreen', this.onHeroVideoNativeExitFullscreen)
        }
        this.$nextTick(() => {
            this.startLayoutReadyWatcher()
            this.updateCurrentSectionByScroll(false)
            // 首次进入页面，无论设备首屏高度如何，先确保 P1 PV 有一次
            this.trackSection('P1')
            this.syncTopicCardVideos()
            // 进入页面自动播放首屏视频（对齐 2026XC70Curling：静音自动播 + 首次交互再解静音）
            this.tryAutoPlayHeroVideoOnEnter()
        })
    },
    beforeDestroy() {
        if (this.observer) this.observer.disconnect()
        if (this.topicCardObserver) this.topicCardObserver.disconnect()
        window.removeEventListener('scroll', this.handleWindowScroll)
        window.removeEventListener('resize', this.handleWindowResize)
        document.removeEventListener('visibilitychange', this.handleVisibilityChange)
        document.removeEventListener('fullscreenchange', this.onHeroVideoFullscreenChange)
        document.removeEventListener('webkitfullscreenchange', this.onHeroVideoFullscreenChange)
        document.removeEventListener('mozfullscreenchange', this.onHeroVideoFullscreenChange)
        document.removeEventListener('MSFullscreenChange', this.onHeroVideoFullscreenChange)
        window.removeEventListener('beforeunload', this.onBeforeUnloadTrackVideoTime)
        const heroVideo = this.$refs.heroVideo
        if (heroVideo) {
            heroVideo.removeEventListener('webkitbeginfullscreen', this.onHeroVideoNativeEnterFullscreen)
            heroVideo.removeEventListener('webkitendfullscreen', this.onHeroVideoNativeExitFullscreen)
        }
        this.unbindTopicCardVideoGestureFallback()
        this.unbindHeroVideoGestureFallback()
        if (this.scrollTimeout) window.clearTimeout(this.scrollTimeout)
        if (this.heroLoadTimer) window.clearTimeout(this.heroLoadTimer)
    },
    computed: {
        showTestTips() {
            return !this.phoneFocused && !this.form.mobile
        },
        isIOS() {
            const ua = navigator.userAgent || ''
            return /iP(hone|od|ad)/i.test(ua)
        },
        heroVideoProgressPercent() {
            if (!this.heroVideoDuration) return 0
            return Math.min(100, Math.max(0, (this.heroVideoCurrentTime / this.heroVideoDuration) * 100))
        },
        heroVideoCurrentTimeText() {
            return this.formatHeroVideoTime(this.heroVideoCurrentTime)
        },
        heroVideoDurationText() {
            return this.formatHeroVideoTime(this.heroVideoDuration)
        }
    },
    methods: {
        fadeStyle(index) {
            return {
                transitionDelay: `${index * 0.05}s`,
                transitionDuration: '0.9s'
            }
        },
        startLayoutReadyWatcher() {
            const heroAssets = [
                this.hero.nav,
                this.hero.title,
                this.hero.videoCover,
                this.hero.videoFrame,
                this.hero.playIcon,
                this.hero.disclaimer,
                backgrounds.hero
            ].filter(Boolean)

            this.heroAssetTotal = heroAssets.length
            this.heroAssetLoaded = 0

            if (!this.heroAssetTotal) {
                this.revealSection(sectionConfigs[0].name)
                return
            }

            if (this.heroLoadTimer) window.clearTimeout(this.heroLoadTimer)
            this.heroLoadTimer = window.setTimeout(() => {
                this.revealSection(sectionConfigs[0].name)
            }, 1600)

            heroAssets.forEach((src) => {
                const img = new Image()
                const done = () => {
                    this.heroAssetLoaded += 1
                    if (this.heroAssetLoaded >= this.heroAssetTotal) {
                        if (this.heroLoadTimer) {
                            window.clearTimeout(this.heroLoadTimer)
                            this.heroLoadTimer = null
                        }
                        this.revealSection(sectionConfigs[0].name)
                    }
                }
                img.onload = done
                img.onerror = done
                img.src = src
                if (img.complete) done()
            })
        },
        handlePhoneFocus() {
            this.phoneFocused = true
        },
        handlePhoneBlur() {
            this.phoneFocused = false
        },
        getCurrentSectionLabel() {
            return this.currentSectionName || 'P1'
        },
        heroFadeStyle(index) {
            return {
                transitionDelay: `${index * 0.24}s`,
                transitionDuration: '0.9s'
            }
        },
        setupTracking() {
            if (!window.IntersectionObserver) return
            if (this.observer) this.observer.disconnect()
            this.observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const sectionName = entry.target.dataset.section
                            this.revealSection(sectionName)
                        }
                    })
                },
                { threshold: 0.28 }
            )

            this.$nextTick(() => {
                sectionConfigs.forEach(({ name }) => {
                    const ref = this.$refs[`section-${name}`]
                    const target = Array.isArray(ref) ? ref[0] : ref
                    if (target) {
                        target.dataset.section = name
                        this.observer.observe(target)
                    }
                })
            })
        },
        setupTopicCardVideoPlayback() {
            this.$nextTick(() => {
                if (this.topicCardObserver) this.topicCardObserver.disconnect()
                const videos = this.$el ? this.$el.querySelectorAll('.topic-card-group__image') : []
                if (!videos.length) return

                if (!window.IntersectionObserver) {
                    videos.forEach((video) => {
                        this.startTopicCardVideo(video)
                    })
                    return
                }

                this.topicCardObserver = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            if (entry.isIntersecting) {
                                this.startTopicCardVideo(entry.target)
                                return
                            }
                            const video = entry.target
                            if (video && !video.paused) video.pause()
                        })
                    },
                    {
                        threshold: 0.01,
                        rootMargin: '120px 0px 120px 0px'
                    }
                )

                videos.forEach((video) => {
                    this.topicCardObserver.observe(video)
                })
            })
        },
        revealSection(sectionName) {
            if (!sectionName || this.sectionVisible[sectionName]) return
            this.$set(this.sectionVisible, sectionName, true)
            this.$nextTick(() => {
                this.syncTopicCardVideos()
            })
        },
        handleVisibilityChange() {
            this.syncTopicCardVideos()
            this.syncHeroVideoByViewport()
        },
        isElementInViewport(target) {
            if (!target || typeof target.getBoundingClientRect !== 'function') return false
            const rect = target.getBoundingClientRect()
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
            return rect.top < viewportHeight && rect.bottom > 0
        },
        syncTopicCardVideos() {
            const videos = this.$el ? this.$el.querySelectorAll('.topic-card-group__image') : []
            if (document.hidden) {
                videos.forEach((video) => {
                    if (!video.paused) video.pause()
                })
                return
            }
            videos.forEach((video) => {
                video.muted = true
                const shouldPlay = video.dataset.activated === 'true' && this.isElementInViewport(video)
                if (shouldPlay) {
                    const playPromise = video.play && video.play()
                    if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch(() => {})
                    }
                    return
                }
                if (!video.paused) video.pause()
            })
        },
        startTopicCardVideo(video) {
            if (!video) return
            video.dataset.activated = 'true'
            video.muted = true
            if (!this.isElementInViewport(video)) {
                if (!video.paused) video.pause()
                return
            }
            const playPromise = video.play && video.play()
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {})
            }
        },
        markTopicCardVideoReady(key) {
            if (!key || this.topicCardVideoReady[key]) return
            this.$set(this.topicCardVideoReady, key, true)
            this.$nextTick(() => {
                this.syncTopicCardVideos()
            })
        },
        bindTopicCardVideoGestureFallback() {
            if (this.topicCardVideoGestureFallbackBound || this.topicCardVideoGestureFallbackDone) return
            this.topicCardVideoGestureFallbackBound = true
            document.addEventListener('click', this.onFirstUserGestureForTopicCardVideos, true)
            document.addEventListener('pointerdown', this.onFirstUserGestureForTopicCardVideos, true)
            document.addEventListener('touchstart', this.onFirstUserGestureForTopicCardVideos, true)
            document.addEventListener('touchend', this.onFirstUserGestureForTopicCardVideos, true)
        },
        unbindTopicCardVideoGestureFallback() {
            if (!this.topicCardVideoGestureFallbackBound) return
            this.topicCardVideoGestureFallbackBound = false
            document.removeEventListener('click', this.onFirstUserGestureForTopicCardVideos, true)
            document.removeEventListener('pointerdown', this.onFirstUserGestureForTopicCardVideos, true)
            document.removeEventListener('touchstart', this.onFirstUserGestureForTopicCardVideos, true)
            document.removeEventListener('touchend', this.onFirstUserGestureForTopicCardVideos, true)
        },
        onFirstUserGestureForTopicCardVideos() {
            if (this.topicCardVideoGestureFallbackDone) return
            this.topicCardVideoGestureFallbackDone = true
            this.unbindTopicCardVideoGestureFallback()
            this.unlockAndSyncTopicCardVideos()
        },
        unlockAndSyncTopicCardVideos() {
            const videos = this.$el ? this.$el.querySelectorAll('.topic-card-group__image') : []
            videos.forEach((video) => {
                if (!video) return
                video.muted = true
                try {
                    const playPromise = video.play && video.play()
                    if (playPromise && typeof playPromise.then === 'function') {
                        playPromise
                            .then(() => {
                                try {
                                    if (video.dataset.activated !== 'true' && !video.paused) video.pause()
                                } catch (e) {}
                            })
                            .catch(() => {})
                    }
                } catch (e) {}
            })
            this.$nextTick(() => {
                this.syncTopicCardVideos()
            })
            window.setTimeout(() => this.syncTopicCardVideos(), 120)
            window.setTimeout(() => this.syncTopicCardVideos(), 360)
        },
        trackSection(sectionName) {
            if (!sectionName || this.lastTrackedSectionName === sectionName) return
            const now = Date.now()
            const hitTimes = (this.trackedSectionHitTimesMap[sectionName] || []).filter((time) => now - time < 3000)
            const totalCount = this.trackedSectionTotalCountMap[sectionName] || 0
            // 首两次曝光（首到 + 首次回看）放宽；之后严格为 3 秒内同 section 仅 1 次
            const isEarlyStageForSection = totalCount < 2
            if ((!isEarlyStageForSection && hitTimes.length >= 1) || (isEarlyStageForSection && hitTimes.length >= 2)) return
            this.lastTrackedSectionName = sectionName
            hitTimes.push(now)
            this.$set(this.trackedSectionHitTimesMap, sectionName, hitTimes)
            this.$set(this.trackedSectionTotalCountMap, sectionName, totalCount + 1)
            try {
                console.log(TRACK_EVENT, '标题进入屏幕触发计数', `触发${sectionName}PV`, 1)
                stm_clicki('send', 'event', TRACK_EVENT, '标题进入屏幕触发计数', `触发${sectionName}PV`, 1)
            } catch (e) {}
        },
        updateCurrentSectionByScroll(shouldTrack = true) {
            // 激活 section 使用中部偏下触发线，避免高屏机型首屏误判到下一屏
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
            const triggerLine = viewportHeight * 0.55
            const previousSectionName = this.currentSectionName
            let activeSectionName = sectionConfigs[0].name
            const currentVisibleState = {}
            const previousVisibleState = this.sectionTriggerVisibleState

            sectionConfigs.forEach(({ name }) => {
                const ref = this.$refs[`trigger-${name}`]
                const target = Array.isArray(ref) ? ref[0] : ref
                if (!target || typeof target.getBoundingClientRect !== 'function') return

                const rect = target.getBoundingClientRect()
                currentVisibleState[name] = rect.top <= viewportHeight && rect.bottom >= 0
                if (rect.top <= triggerLine) {
                    activeSectionName = name
                }
            })

            const sectionJustBecameVisible = !!currentVisibleState[activeSectionName] && !this.sectionTriggerVisibleState[activeSectionName]
            const sectionChanged = activeSectionName !== previousSectionName
            this.sectionTriggerVisibleState = Object.assign({}, this.sectionTriggerVisibleState, currentVisibleState)
            this.currentSectionName = activeSectionName
            this.revealSection(activeSectionName)
            this.syncHeroVideoByViewport()
            if (activeSectionName !== 'P1') {
                this.hasEnteredNonP1Section = true
            }
            if (!shouldTrack) return
            // 需求：上划/下划都可触发，P2~P5 标题（topic-section__title）一露出就打点
            const topicSectionNames = ['P2', 'P3', 'P4', 'P5']
            const revealedTopicSection = topicSectionNames.find((name) => currentVisibleState[name] && !previousVisibleState[name])
            if (revealedTopicSection) {
                this.trackSection(revealedTopicSection)
                return
            }
            if (activeSectionName === 'P1' && !this.hasEnteredNonP1Section && !sectionChanged) return
            if (!sectionJustBecameVisible && !sectionChanged) return
            this.trackSection(activeSectionName)
        },
        syncHeroVideoByViewport() {
            const video = this.$refs.heroVideo
            if (!video) return
            if (document.hidden) {
                if (!video.paused) video.pause()
                return
            }
            if (this.heroVideoIsFullscreen) return
            if (this.isElementInViewport(video)) {
                if (video.paused) {
                    const playPromise = video.play && video.play()
                    if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch(() => {})
                    }
                }
                return
            }
            if (!video.paused) video.pause()
        },
        handleWindowScroll() {
            this.updateCurrentSectionByScroll(true)
            this.syncTopicCardVideos()
            this.testDriveFormHidden = true
            if (this.scrollTimeout) window.clearTimeout(this.scrollTimeout)
            this.scrollTimeout = window.setTimeout(() => {
                this.testDriveFormHidden = false
            }, 300)
        },
        handleWindowResize() {
            this.updateCurrentSectionByScroll(false)
        },
        toggleGuo() {
            this.form.guo = this.form.guo == 1 ? 0 : 1
        },
        getCampaignConfig() {
            let campaignImages = null
            let extraData = null
            const urlParams = new URLSearchParams(window.location.search)
            const campaignImagesFromUrl = urlParams.get('campaign_images')
            const extraDataFromUrl = urlParams.get('extra_data')
            if (campaignImagesFromUrl) campaignImages = JSON.parse(campaignImagesFromUrl)
            if (extraDataFromUrl) extraData = JSON.parse(extraDataFromUrl)

            get('https://campaigns.volvocars.com.cn/campaign/statistic/api/web/v1/apistatistic/cms/get-by-campaign-id.php?campaign_id=2026XC70Balloon')
                .then((res) => {
                    if (res.code !== 0 || !res.data) return
                    const remoteCampaignImages = res.data.campaign_images || {}
                    const remoteExtraData = res.data.extra_data || {}
                    campaignImages = Object.assign({}, remoteCampaignImages, campaignImages || {})
                    extraData = Object.assign({}, remoteExtraData, extraData || {})

                    if (campaignImages['首屏'] && campaignImages['首屏'][0]) this.hero.title = campaignImages['首屏'][0].url

                    // if (campaignImages['尾页'] && campaignImages['尾页'][0]) this.offer.brandBar = campaignImages['尾页'][0].url
                    if (campaignImages['尾页'] && campaignImages['尾页'][0]) this.offer.titleFallback = campaignImages['尾页'][0].url
                    if (campaignImages['尾页'] && campaignImages['尾页'][1]) this.offer.ctaImage = campaignImages['尾页'][1].url
                    if (campaignImages['尾页'] && campaignImages['尾页'][2]) this.offer.copyFallback = campaignImages['尾页'][2].url
                    if (campaignImages['尾页'] && campaignImages['尾页'][3]) this.offer.policyFallback = campaignImages['尾页'][3].url
                    if (campaignImages['尾页'] && campaignImages['尾页'][4]) this.offer.tipsFallback = campaignImages['尾页'][4].url
                    if (campaignImages['尾页'] && campaignImages['尾页'][5]) this.offer.qrFallback = campaignImages['尾页'][5].url
                    if (extraData['即刻预定'] && extraData['即刻预定'].link) this.wechatJumpUrl = extraData['即刻预定'].link
                })
                .catch(() => {})
        },
        tryAutoPlayHeroVideoOnEnter() {
            if (this.heroVideoAutoPlayedOnEnter) return
            this.heroVideoAutoPlayedOnEnter = true
            this.heroVideoShouldAutoplay = true

            // 自动播放必须静音，否则多数浏览器会拦截（内嵌视频同理）
            this.$nextTick(() => {
                this.initHeroVideoForAutoplay()
                // 某些浏览器会拦截自动播放：绑定一次“首个用户手势”兜底（点任意位置只会触发播放，不会触发暂停）
                this.bindHeroVideoGestureFallback()
                // 尝试多次（有些机型需要等资源缓冲起来）
                window.setTimeout(() => this.tryPlayHeroVideoMuted(), 0)
                window.setTimeout(() => this.tryPlayHeroVideoMuted(), 200)
                window.setTimeout(() => this.tryPlayHeroVideoMuted(), 600)
            })
        },
        initHeroVideoForAutoplay() {
            const video = this.$refs.heroVideo
            if (!video) return
            this.heroVideoUseNativeControls = this.isIOS
            this.heroVideoFirstInteraction = true
            this.heroVideoMuted = true
            video.muted = true
            video.setAttribute('muted', '')
            // 从头开始
            try {
                video.currentTime = 0
            } catch (e) {}
        },
        tryPlayHeroVideoMuted() {
            const video = this.$refs.heroVideo
            if (!video) return
            if (!this.heroVideoShouldAutoplay) return
            if (!this.isElementInViewport(video)) return
            // 自动播阶段始终保持静音
            if (!video.muted) video.muted = true
            const p = video.play && video.play()
            if (p && typeof p.catch === 'function') p.catch(() => {})
        },
        onHeroVideoLoadedData() {
            // 对齐 Curling：loadeddata 后再触发一次自动播
            this.tryPlayHeroVideoMuted()
        },
        onHeroVideoCanPlay() {
            // 可播放时再触发一次自动播
            this.tryPlayHeroVideoMuted()
        },
        onHeroVideoOverlayClick() {
            this.onFirstUserGestureForHeroVideo()
        },
        onPageFirstGesture() {
            // 上提一层兜底：任意区域首个手势都可尝试解锁视频播放
            this.onFirstUserGestureForHeroVideo()
            this.onFirstUserGestureForTopicCardVideos()
        },
        tryPlayHeroVideoAfterGesture() {
            const video = this.$refs.heroVideo
            if (!video) return
            const playSafely = () => {
                if (!this.isElementInViewport(video)) return
                const p = video.play && video.play()
                if (p && typeof p.catch === 'function') p.catch(() => {})
            }
            playSafely()
            // 个别机型第一次手势后仍会失败，做短间隔重试
            window.setTimeout(playSafely, 120)
            window.setTimeout(playSafely, 360)
        },
        bindHeroVideoGestureFallback() {
            if (this.heroVideoGestureFallbackBound || this.heroVideoGestureFallbackDone) return
            this.heroVideoGestureFallbackBound = true
            // capture=true：尽量在各类阻止冒泡的组件之前拿到点击
            document.addEventListener('click', this.onFirstUserGestureForHeroVideo, true)
            document.addEventListener('pointerdown', this.onFirstUserGestureForHeroVideo, true)
            document.addEventListener('touchstart', this.onFirstUserGestureForHeroVideo, true)
            document.addEventListener('touchend', this.onFirstUserGestureForHeroVideo, true)
        },
        unbindHeroVideoGestureFallback() {
            if (!this.heroVideoGestureFallbackBound) return
            this.heroVideoGestureFallbackBound = false
            document.removeEventListener('click', this.onFirstUserGestureForHeroVideo, true)
            document.removeEventListener('pointerdown', this.onFirstUserGestureForHeroVideo, true)
            document.removeEventListener('touchstart', this.onFirstUserGestureForHeroVideo, true)
            document.removeEventListener('touchend', this.onFirstUserGestureForHeroVideo, true)
        },
        onFirstUserGestureForHeroVideo() {
            if (this.heroVideoGestureFallbackDone) return
            const video = this.$refs.heroVideo
            if (!video) return

            // 兜底的目标：只“播放”，不要“暂停”
            // - 已经在播：不做任何事
            // - 暂停中：解静音并尝试播放
            if (!video.paused) {
                this.heroVideoGestureFallbackDone = true
                this.unbindHeroVideoGestureFallback()
                return
            }

            // 首次用户手势：解静音（与 Curling 的 firstVideo 一致）
            this.heroVideoMuted = false
            video.muted = false
            this.heroVideoFirstInteraction = false
            this.heroVideoShowOverlay = false
            this.heroVideoShouldAutoplay = false

            this.tryPlayHeroVideoAfterGesture()
        },
        // ====== Hero 视频控制（对齐 2026XC70Curling/videoJ 核心逻辑）======
        formatHeroVideoTime(seconds) {
            const safe = Number.isFinite(seconds) ? seconds : 0
            const min = Math.floor(safe / 60)
            const sec = Math.floor(safe % 60)
            return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
        },
        onHeroVideoLoadedMeta(e) {
            const video = e && e.target ? e.target : this.$refs.heroVideo
            if (!video) return
            this.heroVideoDuration = video.duration || 0
            this.heroVideoCurrentTime = video.currentTime || 0
        },
        onHeroVideoTimeUpdate(e) {
            const video = e && e.target ? e.target : this.$refs.heroVideo
            if (!video) return
            this.heroVideoCurrentTime = video.currentTime || 0
            // 对齐 Curling：播放一小段后隐藏封面遮罩
            if (this.heroVideoCurrentTime >= 0.2 && this.heroVideoShowOverlay) {
                this.heroVideoShowOverlay = false
            }
        },
        trackHeroVideoPlayCount() {
            try {
                console.log(TRACK_EVENT, '视频播放计数（自动+触发）', '视频播放计数', 1)
                stm_clicki('send', 'event', TRACK_EVENT, '视频播放计数（自动+触发）', '视频播放计数', 1)
            } catch (e) {}
        },
        onHeroVideoPlay() {
            this.heroVideoPaused = false
            this.trackHeroVideoPlayCount()
            // 一旦真正开始播放，立即隐藏遮罩（避免“其实在播但看不到”）
            if (this.heroVideoShowOverlay) this.heroVideoShowOverlay = false
            // 真正播放起来后，兜底监听就不需要了
            this.heroVideoGestureFallbackDone = true
            this.unbindHeroVideoGestureFallback()
        },
        onHeroVideoPause() {
            this.heroVideoPaused = true
        },
        onHeroVideoEnded() {
            this.heroVideoPaused = true
        },
        onHeroVideoProgressClick(e) {
            const video = this.$refs.heroVideo
            if (!video || !this.heroVideoDuration) return
            const rect = e.currentTarget.getBoundingClientRect()
            const clickX = e.clientX - rect.left
            const percent = rect.width ? clickX / rect.width : 0
            video.currentTime = Math.max(0, Math.min(this.heroVideoDuration, percent * this.heroVideoDuration))
        },
        toggleHeroVideoPlayPause() {
            const video = this.$refs.heroVideo
            if (!video) return

            // Curling：第一次交互时自动解静音
            if (this.heroVideoFirstInteraction) {
                this.heroVideoMuted = false
                video.muted = false
                this.heroVideoFirstInteraction = false
            }

            if (video.paused) {
                video.play().catch(() => {})
            } else {
                video.pause()
            }
        },
        toggleHeroVideoMute() {
            const video = this.$refs.heroVideo
            if (!video) return
            video.muted = !video.muted
            this.heroVideoMuted = video.muted
            if (!video.muted) this.heroVideoFirstInteraction = false
        },
        toggleHeroVideoFullscreen() {
            const video = this.$refs.heroVideo
            if (!video) return

            // 与 Curling 一致：多浏览器兼容 + iOS webkitEnterFullscreen
            try {
                if (video.requestFullscreen) {
                    video.requestFullscreen()
                } else if (video.webkitRequestFullscreen) {
                    video.webkitRequestFullscreen()
                } else if (video.mozRequestFullscreen) {
                    video.mozRequestFullscreen()
                } else if (video.msRequestFullscreen) {
                    video.msRequestFullscreen()
                } else if (video.webkitEnterFullscreen) {
                    video.webkitEnterFullscreen()
                }
            } catch (e) {}

            try {
                console.log(TRACK_EVENT, '全屏播放按钮点击', '全屏播放按钮点击', 1)
                stm_clicki('send', 'event', TRACK_EVENT, '全屏播放按钮点击', '全屏播放按钮点击', 1)
            } catch (e) {}
        },
        onHeroVideoFullscreenChange() {
            const doc = document
            const isFull =
                !!doc.fullscreenElement ||
                !!doc.webkitFullscreenElement ||
                !!doc.mozFullScreenElement ||
                !!doc.msFullscreenElement ||
                !!doc.fullscreen ||
                !!doc.webkitIsFullScreen ||
                !!doc.mozFullScreen
            this.heroVideoIsFullscreen = !!isFull
        },
        onHeroVideoNativeEnterFullscreen() {
            this.heroVideoIsFullscreen = true
            try {
                console.log(TRACK_EVENT, '全屏播放按钮点击', '全屏播放按钮点击', 1)
                stm_clicki('send', 'event', TRACK_EVENT, '全屏播放按钮点击', '全屏播放按钮点击', 1)
            } catch (e) {}
        },
        onHeroVideoNativeExitFullscreen() {
            this.heroVideoIsFullscreen = false
        },
        onBeforeUnloadTrackVideoTime() {
            try {
                console.log(TRACK_EVENT, '视频播放时长', Math.floor(this.heroVideoCurrentTime) + 's', 1)
                stm_clicki('send', 'event', TRACK_EVENT, '视频播放时长', Math.floor(this.heroVideoCurrentTime) + 's', 1)
            } catch (e) {}
        },
        goUrl() {
            try {
                console.log(TRACK_EVENT, '即刻下定按钮点击', '点击即刻下定按钮', 1)
                stm_clicki('send', 'event', TRACK_EVENT, '即刻下定按钮点击', '点击即刻下定按钮', 1)
            } catch (e) {}
            window.location.href = this.wechatJumpUrl
        },
        toXieyi() {
            try {
                console.log(TRACK_EVENT, '底部浮窗留资隐私条款点击', `${this.getCurrentSectionLabel()}点击隐私条款`, 1)
                stm_clicki('send', 'event', TRACK_EVENT, '底部浮窗留资隐私条款点击', `${this.getCurrentSectionLabel()}点击隐私条款`, 1)
            } catch (e) {}
            window.location.href = './yszc.html'
        },
        submit() {
            try {
                console.log(TRACK_EVENT, '底部浮窗留资提交按钮点击', `${this.getCurrentSectionLabel()}点击提交按钮`, 1)
                stm_clicki('send', 'event', TRACK_EVENT, '底部浮窗留资提交按钮点击', `${this.getCurrentSectionLabel()}点击提交按钮`, 1)
            } catch (e) {}
            if (!this.form.mobile) return this.$alert('请输入手机号')
            if (!isPhoneNumber(this.form.mobile)) return this.$alert('手机号格式错误')
            if (!this.form.guo) return this.$alert('您还未同意隐私政策')

            let utm_campaign = getUrlParam('utm_campaign')
            let utm_source = getUrlParam('utm_source')
            if (!utm_campaign) utm_campaign = 'WXH5-CAMP-DIRECT'
            if (!utm_source) utm_source = 'WXH5-CAMP-DIRECT'

            const data = {
                username: '客户',
                userphone: this.form.mobile,
                cartype: 'xc70',
                utm_campaign,
                utm_source,
                source: '7'
            }

            post('https://campaigns.volvocars.com.cn/campaign/statistic/api/web/v1/apistatistic/drivers/save.php', data)
                .then((res) => {
                    if (res.code === 0) {
                        this.showSuccess = true
                        this.form.mobile = ''
                        this.form.guo = 0
                        window.setTimeout(() => {
                            this.showSuccess = false
                        }, 2000)
                        try {
                            console.log(TRACK_EVENT, '底部浮窗留资表单提交计数', '提交表单成功', 1)
                            stm_clicki('send', 'event', TRACK_EVENT, '底部浮窗留资表单提交计数', '提交表单成功', 1)
                        } catch (e) {}
                        return
                    }
                    try {
                        console.log(TRACK_EVENT, '底部浮窗留资表单提交计数', '提交表单未成功', 1)
                        stm_clicki('send', 'event', TRACK_EVENT, '底部浮窗留资表单提交计数', '提交表单未成功', 1)
                    } catch (e) {}
                    this.$alert('提交未成功')
                })
                .catch(() => {
                    try {
                        console.log(TRACK_EVENT, '底部浮窗留资表单提交计数', '提交表单未成功', 1)
                        stm_clicki('send', 'event', TRACK_EVENT, '底部浮窗留资表单提交计数', '提交表单未成功', 1)
                    } catch (e) {}
                    this.$alert('提交未成功')
                })
        }
    }
}
</script>

<style scoped lang="scss">
.indexPage {
    min-height: 100vh;
    background: #ececec;
    color: #0f1720;
    // padding-bottom: 170px;
}

.page-shell {
    width: 100%;
    max-width: 750px;
    margin: 0 auto;
}

.page-shell {
    position: relative;
    min-height: 8527px;
}

.page-background {
    position: absolute;
    inset: 0 0 auto;
    width: 100%;
    z-index: 1;
}

.page-background__image {
    display: block;
    width: 100%;
}

.page-foreground {
    position: relative;
    z-index: 2;
    padding-top: 54px;
}

.content-section {
    width: 100%;
    box-sizing: border-box;
}


.hero-section__nav,
.hero-section__title,
.hero-section__disclaimer,
.topic-section__title,
.topic4-section__brand,
.topic4-section__title,
.topic4-section__copy,
.topic4-section__policy,
.topic4-section__tips {
    display: block;
    width: 100%;
}

.hero-section__title {
    margin-top: 40px;
}

.hero-section__title-wrap {
    margin-top: 40px;
    min-height: 719px;
}

.hero-video-group {
    margin: 0 auto 17px;
    width: 625px;
}

/* 首屏内嵌视频容器（Curling 同款“视频块”思路） */
.hero-inline-video {
    position: relative;
    width: 100%;
    padding-top: 56.25%;
    overflow: hidden;
    border: 2px solid #fff;
    box-sizing: border-box;
    background: #000;
}

.topic-section {
    padding: 0 47px;
}

.topic-section__title {
    display: block;
    width: calc(100% + 94px);
    margin: 0 -47px;
}

.topic-group {
    position: relative;
    width: 655px;
    height: 368px;
    margin-left: auto;
    margin-right: auto;
}

.topic-card-group__fallback,
.topic-card-group__image,
.topic-card-group__floating-word {
    display: block;
    width: 100%;
}

.topic-card-group__fallback,
.topic-card-group__image {
    position: absolute;
    inset: 0;
    height: 100%;
    object-fit: cover;
    border-radius: 20px;
}

.topic-card-group__fallback {
    z-index: 1;
}

.topic-card-group__image {
    background: #000;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.28s ease;
}

.topic-card-group__image--ready {
    opacity: 1;
}

.topic-card-group__floating-word {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 22px;
    // height: 43px;
    z-index: 3;
}

.topic-section--1 {
    margin-top: 265px;
}

.topic-section--1 .topic-section__title {
    margin-bottom: 638px;
}

.topic-section--1 .topic-group--243 {
    // margin-bottom: 16px;
}

.topic-section--2 {
    margin-top: 96px;
}

.topic-section--2 .topic-section__title {
    margin-bottom: 635px;
}

.topic-section--2 .topic-group--245 {
    // margin-bottom: 16px;
}

.topic-section--3 {
    margin-top: 96px;
}

.topic-section--3 .topic-section__title {
    margin-bottom: 638px;
}

.topic-section--3 .topic-group--247 {
    // margin-bottom: 16px;
}

.topic-section--4 {
    margin-top: 96px;
}

.topic-section--4 .topic-section__title {
    margin-bottom: 638px;
}

.topic-section--4 .topic-group--249 {
    // margin-bottom: 16px;
}

.topic4-section {
    padding: 96px 0 301px;
}

.topic4-group {
    position: relative;
}

.topic4-group img {
    display: block;
    width: 100%;
}

.topic4-section__title {
    margin: 0;
}

.topic4-group--242 {
    margin-top: 48px;
    min-height: 1015px;
}

.topic4-section__cta {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    margin-bottom: 29px;
}

.topic4-section__copy-wrap {
    min-height: 230px;
}

.topic4-group--action {
}

.topic4-group--benefits {
    margin-top: 47px;
}

.topic4-group--qr {
    margin-top: 52px;
}

.topic4-section__qr-box {
    width: 211px;
    margin: 51px auto 0;
}

.topic4-section__qr {
    margin: 0;
}

.fade-item {
    opacity: 0;
    transform: translate3d(0, 30px, 0);
    transition: opacity 0.9s ease, transform 0.9s ease;
}

.section-visible .fade-item {
    opacity: 1;
    transform: translate3d(0, 0, 0);
}


.fixedBox {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 55;
    padding: 0 16px calc(16px + env(safe-area-inset-bottom));
    pointer-events: none;
}

.testDriveForm {
    position: relative;
    width: 100%;
    max-width: 690px;
    height: 126px;
    margin: 0 auto;
    background: url(https://campaigns-static.volvocars.com.cn/image/images/20251113XC70lanuch_test_1_03.png) 0 0 no-repeat;
    background-size: 100% 100%;
    pointer-events: auto;
    transition: opacity 0.7s;
}
.testDriveForm .testTips {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/20251113XC70lanuch_test_2_03.png) 0 0 no-repeat;
    background-size: 100% auto;
    position: absolute;
    width: 100%;
    height: 60px;
    left: 0;
    top: 0;
}

.testDriveForm.hidden {
    opacity: 0.1;
}

.testTips {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.form-phone {
    position: absolute;
    left: 145px;
    top: 12px;
    width: 292px;
    height: 46px;
}

.form-phone input {
    display: block;
    width: 100%;
    height: 100%;
    background: transparent;
    border: 0;
    padding: 0;
    color: #0f1720;
    font-size: 30px;
    font-family: 'Volvo Centum', Verdana, Geneva, sans-serif;
    appearance: none;
    -webkit-appearance: none;
}

.testDriveForm .guo {
    position: absolute;
    width: 30px;
    height: 36px;
    left: 43px;
    top: 76px;
    z-index: 100;
    cursor: pointer;
}

.testDriveForm .guo.active {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/20251113XC70lanuch_products-guo.png) no-repeat 50% 32%;
    background-size: 80% auto;
}

.xieyi {
    position: absolute;
    width: 175px;
    height: 40px;
    left: 176px;
    top: 76px;
}

.drive-btn {
    position: absolute;
    width: 190px;
    height: 72px;
    right: 20px;
    top: 24px;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
}

.hero-video {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: block;
    background: #000;
    object-fit: cover;
}

/* 封面遮罩（点击后隐藏） */
.hero-video-overlay {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-position: 0 0;
    background-size: 100% auto;
    z-index: 3;
}

.hero-video-frame {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 4;
    pointer-events: none;
}

.hero-video-play {
    position: absolute;
    width: 105px;
    height: 105px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    pointer-events: none;
}

/* ========== Hero 内嵌视频控制条样式（对齐 2026XC70Curling）========== */
.hero-inline-video .hero-controls-container {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
    padding: 10px;
    display: flex;
    flex-direction: column;
    transition: opacity 0.3s;
    box-sizing: border-box;
    z-index: 6;
}

.hero-inline-video .progress-container {
    width: 100%;
    height: 5px;
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    margin-bottom: 10px;
    cursor: pointer;
    position: relative;
}

.hero-inline-video .progress-bar {
    height: 100%;
    background: linear-gradient(to right, #999999, #ffffff);
    border-radius: 4px;
    width: 0%;
    transition: width 0.1s;
}

.hero-inline-video .controls-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.hero-inline-video .left-controls,
.hero-inline-video .right-controls {
    display: flex;
    align-items: center;
}

.hero-inline-video .control-btn {
    background: none;
    border: none;
    color: white;
    font-size: 18px;
    cursor: pointer;
    transition: all 0.2s;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    padding: 0;
}

.hero-inline-video .control-btn.play-pause.playing {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/paused_03.png) center center no-repeat;
    background-size: 50%;
}

.hero-inline-video .control-btn.play-pause.paused {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/play_03.png) center center no-repeat;
    background-size: 50%;
}

.hero-inline-video .time-display {
    color: white;
    font-size: 14px;
    margin: 0 2px;
    line-height: 1;
    user-select: none;
}

.hero-inline-video .volume-container {
    display: flex;
    align-items: center;
}

.hero-inline-video .fullscreen-btn {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/big_03.png) center center no-repeat;
    background-size: 50%;
}

.hero-inline-video .fullscreen-btn.full {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/small_03.png) center center no-repeat;
    background-size: 50%;
}

.hero-inline-video .volume-btn.mutedFalse {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/volume2_03.png) center center no-repeat;
    background-size: 50%;
}

.hero-inline-video .volume-btn.mutedTrue {
    background: url(https://campaigns-static.volvocars.com.cn/image/images/volume1_03.png) center center no-repeat;
    background-size: 50%;
}

.toast-success {
    position: fixed;
    inset: 0;
    z-index: 90;
    background: rgba(0, 0, 0, 0.78);
}

.toast-success-con {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 322px;
}

.toast-success-con img {
    display: block;
    width: 100%;
}
</style>
